import { useEffect, useRef, useState } from "react";
import { useLeaders } from "@/context/LeadersContext";

const BADGES = {
  top_surfer: {
    emoji: "🏄",
    bg: "from-cyan-500 to-blue-600",
    title: "Top Surfer",
    describe: (v) => `Most sites surfed platform-wide${v ? ` — ${v.toLocaleString()} surfs and counting.` : ""}`,
    testid: "badge-top-surfer",
  },
  top_referrer: {
    emoji: "🌱",
    bg: "from-emerald-500 to-green-700",
    title: "Top Referrer",
    describe: (v) => `Recruited the most active members${v ? ` — ${v.toLocaleString()} direct referrals.` : ""}`,
    testid: "badge-top-referrer",
  },
  top_poster: {
    emoji: "🎙️",
    bg: "from-yellow-500 to-orange-600",
    title: "Top Poster",
    describe: (v) => `Most posts on the ad feed${v ? ` — ${v.toLocaleString()} published posts.` : ""}`,
    testid: "badge-top-poster",
  },
};

function Pill({ kind, value, size }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const meta = BADGES[kind];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-block align-middle">
      <button
        type="button"
        data-testid={meta.testid}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        title={meta.title}
        aria-label={meta.title}
        className={`inline-flex items-center justify-center rounded-full bg-gradient-to-br ${meta.bg} text-white shadow-sm hover:scale-110 transition-transform duration-150 cursor-help`}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.65), lineHeight: 1 }}
      >
        <span aria-hidden="true">{meta.emoji}</span>
      </button>
      {open && (
        <div
          data-testid={`${meta.testid}-popover`}
          className="absolute z-40 left-1/2 -translate-x-1/2 top-full mt-2 w-64 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-4 text-left normal-case tracking-normal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full bg-gradient-to-br ${meta.bg} text-white text-base shrink-0`}>
              {meta.emoji}
            </span>
            <span className="font-bold text-slate-900 dark:text-white text-sm tracking-normal leading-tight">
              {meta.title}
            </span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed tracking-normal m-0">
            {meta.describe(value)}
          </p>
        </div>
      )}
    </span>
  );
}

/**
 * Renders the achievement badges (top surfer / referrer / poster) that apply
 * to the given user. Each badge is a small pill with a click-to-open popover
 * describing what it means. Renders nothing when the user has no achievements.
 */
export default function AchievementBadges({ userId, size = 16, className = "" }) {
  const { leaders } = useLeaders();
  if (!userId || !leaders) return null;
  const applies = [];
  if (leaders.top_surfer?.id === userId) applies.push(["top_surfer", leaders.top_surfer.value]);
  if (leaders.top_referrer?.id === userId) applies.push(["top_referrer", leaders.top_referrer.value]);
  if (leaders.top_poster?.id === userId) applies.push(["top_poster", leaders.top_poster.value]);
  if (applies.length === 0) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 align-middle ${className}`}>
      {applies.map(([kind, value]) => (
        <Pill key={kind} kind={kind} value={value} size={size} />
      ))}
    </span>
  );
}
